import { HubConnection, HubConnectionBuilder, HubConnectionState, IRetryPolicy, RetryContext } from '@microsoft/signalr';

// The default policy gives up after 60 seconds. A results page is left open on a
// phone for the length of a race, so we keep retrying indefinitely instead.
const retryPolicy: IRetryPolicy = {
	nextRetryDelayInMilliseconds(context: RetryContext): number {
		if (context.previousRetryCount < 4) return 2000;
		if (context.previousRetryCount < 10) return 5000;
		return 15000;
	}
};

export interface RaceHubOptions {
	raceId: string;

	/** Called for every message pushed on the group. */
	onCheckpointAdded: (checkpoint: any, competitor: any) => void;

	/**
	 * Called after every reconnect, and whenever the tab comes back to the
	 * foreground. Anything broadcast while we were disconnected is gone for good,
	 * so the caller must refetch from the API here.
	 */
	onResync: () => void;
}

/**
 * Connects to the checkpoint hub for a race and keeps that connection alive
 * across mobile tab suspension.
 *
 * Returns a dispose function; callers must invoke it on unmount.
 */
export function connectRaceHub(options: RaceHubOptions): () => void {
	const { raceId, onCheckpointAdded, onResync } = options;
	const group = 'Race' + raceId;

	const connection: HubConnection = new HubConnectionBuilder()
		.withUrl('/checkpointhub')
		.withAutomaticReconnect(retryPolicy)
		.build();

	connection.on('CheckpointAdded', onCheckpointAdded);

	// Groups are keyed by connection id, and a reconnect issues a new one, so
	// membership has to be re-established every time.
	const joinGroup = () => connection.invoke('JoinGroup', group)
		.catch(err => console.error('SignalR JoinGroup failed:', err));

	let disposed = false;

	connection.onreconnected(() => {
		joinGroup().then(onResync);
	});

	const start = () => {
		if (disposed) return;
		if (connection.state !== HubConnectionState.Disconnected) return;

		connection.start()
			.then(joinGroup)
			.catch(err => {
				console.error('SignalR connection error:', err);
				// withAutomaticReconnect only covers drops after a successful start,
				// so the initial connect needs its own retry.
				if (!disposed) setTimeout(start, 5000);
			});
	};

	// Mobile browsers suspend background tabs and do not reliably fire onclose, so
	// the connection can sit in a zombie Connected state. Resync on every
	// foreground and restart the connection if it actually died.
	const onVisibilityChange = () => {
		if (document.visibilityState !== 'visible' || disposed) return;

		if (connection.state === HubConnectionState.Disconnected) {
			start();
		}

		onResync();
	};

	document.addEventListener('visibilitychange', onVisibilityChange);

	start();

	return () => {
		disposed = true;
		document.removeEventListener('visibilitychange', onVisibilityChange);
		connection.stop().catch(err => console.error('SignalR stop failed:', err));
	};
}
